/**
 * SupplyChainFlow — Horizontal node graph shown above every demo tab.
 *
 * 6 nodes: Label → Manufacturer → Warehouse → Warehouse (alt) →
 * Retailers → Consumers. When a user runs an insight resolve or an
 * approval, the affected nodes briefly pulse (via
 * `flashNodes(nodeIds)` on the shared context).
 */
import React from 'react';
import { Mic2, Factory, Warehouse, Store, Users, Globe } from 'lucide-react';
import { useDistributionDemo } from '../DistributionDemoContext';

const ICON_BY_ROLE = {
    label: Mic2,
    manufacturer: Factory,
    warehouse: Warehouse,
    warehouse_alt: Globe,
    retailers: Store,
    consumers: Users,
};

const ACCENT_CLASSES = {
    indigo: 'bg-indigo-500 ring-indigo-300',
    slate: 'bg-slate-700 ring-slate-400',
    emerald: 'bg-emerald-500 ring-emerald-300',
    cyan: 'bg-cyan-500 ring-cyan-300',
    amber: 'bg-amber-500 ring-amber-300',
    pink: 'bg-pink-500 ring-pink-300',
};

export default function SupplyChainFlow({ nodes = [] }) {
    const { highlightedNodes } = useDistributionDemo();

    if (!nodes.length) return null;

    return (
        <div
            data-testid="supply-chain-flow"
            className="rounded-xl border border-slate-200 bg-white px-4 py-5 overflow-x-auto"
        >
            <div className="flex items-center gap-2 min-w-[720px]">
                {nodes.map((node, idx) => {
                    const Icon = ICON_BY_ROLE[node.node_id] || Warehouse;
                    const active = highlightedNodes.includes(node.node_id);
                    const accent = ACCENT_CLASSES[node.accent] || ACCENT_CLASSES.slate;
                    return (
                        <React.Fragment key={node.node_id}>
                            <div
                                data-testid={`supply-node-${node.node_id}`}
                                className={`flex-1 min-w-[108px] flex flex-col items-center text-center transition-transform duration-500 ${active ? 'scale-105' : ''}`}
                            >
                                <div
                                    className={`relative h-11 w-11 rounded-full ${accent} text-white flex items-center justify-center shadow-sm transition-all ${active ? 'ring-4 animate-pulse' : 'ring-2 ring-transparent'}`}
                                >
                                    <Icon className="h-5 w-5" />
                                </div>
                                <div className="mt-2 text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                                    {node.role}
                                </div>
                                <div className="text-xs font-medium text-slate-900 leading-tight mt-0.5 max-w-[140px]">
                                    {node.name}
                                </div>
                            </div>
                            {idx < nodes.length - 1 && (
                                <div className="flex-shrink-0 flex items-center">
                                    <div className={`h-px w-6 sm:w-10 ${active || highlightedNodes.includes(nodes[idx + 1]?.node_id) ? 'bg-slate-900' : 'bg-slate-200'} transition-colors`} />
                                </div>
                            )}
                        </React.Fragment>
                    );
                })}
            </div>
        </div>
    );
}
